/**
 * Main App Component
 *
 * Sets up routing and authentication context for the entire application.
 *
 * Routes:
 * - / - Landing page (public)
 * - /login - Login/signup page (public only)
 * - /dashboard - Main app (protected)
 * - /account - User account settings (protected)
 * - /account/billing - Billing & credits management (protected)
 *
 * Security:
 * - AuthProvider wraps entire app for global auth state
 * - ProtectedRoute guards authenticated pages
 * - PublicOnlyRoute prevents access to auth pages when logged in
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, PublicOnlyRoute } from './components/auth/ProtectedRoute';
import Header from './components/layout/Header';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AccountPage from './pages/AccountPage';
import BillingPage from './pages/BillingPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import AdminFeedbackPage from './pages/AdminFeedbackPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

// Styles
import './App.css';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          <Route
            path="/login"
            element={
              <PublicOnlyRoute>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />

          {/* Auth Callback Route - handles email verification */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <>
                  <Header />
                  <DashboardPage />
                </>
              </ProtectedRoute>
            }
          />

          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <>
                  <Header />
                  <AccountPage />
                </>
              </ProtectedRoute>
            }
          />

          <Route
            path="/account/billing"
            element={
              <ProtectedRoute>
                <>
                  <Header />
                  <BillingPage />
                </>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/feedback"
            element={
              <ProtectedRoute>
                <>
                  <Header />
                  <AdminFeedbackPage />
                </>
              </ProtectedRoute>
            }
          />

          {/* Fallback - redirect to landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
